/**
 * Router configuration — QA #27
 *
 * Centralizes all route definitions to reduce the ~70 lazy imports
 * scattered across router.tsx. Each route entry declares its path,
 * component factory, and optional parent/children. The Router component
 * in router.tsx reads this registry and renders the route tree.
 *
 * Benefits:
 *   - Single source of truth for all routes
 *   - Easy to add/remove routes without touching JSX
 *   - Type-safe route paths (can add route helper later)
 *   - Lazy imports co-located with their path definitions
 */

import type { ComponentType } from 'react';

export interface RouteConfig {
  path: string;
  component: () => Promise<{ default: ComponentType }>;
  children?: RouteConfig[];
  index?: boolean;
}

export const routes: RouteConfig[] = [
  // Auth routes (no layout)
  {
    path: '/login',
    component: () => import('@/features/auth/LoginPage').then((m) => ({ default: m.LoginPage })),
  },

  // Protected routes (wrapped in AuthGuard + AdminLayout)
  {
    path: '/',
    component: () =>
      import('@/features/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })),
    index: true,
  },

  // Orders
  {
    path: '/orders',
    component: () =>
      import('@/features/orders/OrdersListPage').then((m) => ({ default: m.OrdersListPage })),
  },
  {
    path: '/orders/:id',
    component: () =>
      import('@/features/orders/OrderDetailPage').then((m) => ({ default: m.OrderDetailPage })),
  },

  // Products
  {
    path: '/products',
    component: () =>
      import('@/features/products/ProductsListPage').then((m) => ({ default: m.ProductsListPage })),
  },
  {
    path: '/products/new',
    component: () =>
      import('@/features/products/ProductCreatePage').then((m) => ({
        default: m.ProductCreatePage,
      })),
  },
  {
    path: '/products/:id',
    component: () =>
      import('@/features/products/ProductDetailPage').then((m) => ({
        default: m.ProductDetailPage,
      })),
  },
  {
    path: '/products/:id/edit',
    component: () =>
      import('@/features/products/ProductEditPage').then((m) => ({ default: m.ProductEditPage })),
  },

  // Inventory
  {
    path: '/inventory',
    component: () =>
      import('@/features/inventory/InventoryPage').then((m) => ({ default: m.InventoryPage })),
  },

  // Customers
  {
    path: '/customers',
    component: () =>
      import('@/features/customers/CustomersListPage').then((m) => ({
        default: m.CustomersListPage,
      })),
  },
  {
    path: '/customers/:id',
    component: () =>
      import('@/features/customers/CustomerDetailPage').then((m) => ({
        default: m.CustomerDetailPage,
      })),
  },

  // Promotions
  {
    path: '/promotions',
    component: () =>
      import('@/features/promotions/PromotionsListPage').then((m) => ({
        default: m.PromotionsListPage,
      })),
  },
  {
    path: '/promotions/:id',
    component: () =>
      import('@/features/promotions/PromotionEditPage').then((m) => ({
        default: m.PromotionEditPage,
      })),
  },

  // Reviews
  {
    path: '/reviews',
    component: () =>
      import('@/features/reviews/ReviewsListPage').then((m) => ({ default: m.ReviewsListPage })),
  },

  // Newsletter (Listmonk)
  {
    path: '/newsletter',
    component: () =>
      import('@/features/marketing/NewsletterPage').then((m) => ({ default: m.NewsletterPage })),
  },

  // Referral & Club Privilège
  {
    path: '/referrals',
    component: () =>
      import('@/features/marketing/ReferralsPage').then((m) => ({ default: m.ReferralsPage })),
  },

  // Draft Orders
  {
    path: '/draft-orders',
    component: () =>
      import('@/features/draft-orders/DraftOrdersListPage').then((m) => ({
        default: m.DraftOrdersListPage,
      })),
  },

  // Returns
  {
    path: '/returns',
    component: () =>
      import('@/features/returns/ReturnsListPage').then((m) => ({ default: m.ReturnsListPage })),
  },

  // Categories
  {
    path: '/categories',
    component: () =>
      import('@/features/categories/CategoriesListPage').then((m) => ({
        default: m.CategoriesListPage,
      })),
  },
  {
    path: '/categories/new',
    component: () =>
      import('@/features/categories/CategoryCreatePage').then((m) => ({
        default: m.CategoryCreatePage,
      })),
  },
  {
    path: '/categories/:id',
    component: () =>
      import('@/features/categories/CategoryDetailPage').then((m) => ({
        default: m.CategoryDetailPage,
      })),
  },
  {
    path: '/categories/:id/edit',
    component: () =>
      import('@/features/categories/CategoryEditPage').then((m) => ({
        default: m.CategoryEditPage,
      })),
  },

  // Settings (nested routes)
  //
  // Starter France B2C furniture business: only the essentials.
  // Removed: API keys, files, notifications, notification log,
  // email templates, sales channels, multi-region/country/language.
  {
    path: '/settings',
    component: () =>
      import('@/features/settings/SettingsLayout').then((m) => ({ default: m.SettingsLayout })),
    children: [
      {
        path: '',
        index: true,
        component: () =>
          import('@/features/settings/SettingsOverview').then((m) => ({
            default: m.SettingsOverview,
          })),
      },
      {
        path: 'overview',
        component: () =>
          import('@/features/settings/SettingsOverview').then((m) => ({
            default: m.SettingsOverview,
          })),
      },
      {
        path: 'roles',
        component: () =>
          import('@/features/settings/roles/RolesPage').then((m) => ({
            default: m.RolesPage,
          })),
      },
      {
        path: 'notifications',
        component: () =>
          import('@/features/settings/notifications/NotificationsPage').then((m) => ({
            default: m.NotificationsPage,
          })),
      },
      {
        path: 'tax',
        component: () =>
          import('@/features/settings/tax/TaxPage').then((m) => ({ default: m.TaxPage })),
      },
      {
        path: 'shipping',
        component: () =>
          import('@/features/settings/shipping/ShippingPage').then((m) => ({
            default: m.ShippingPage,
          })),
      },
      {
        path: 'account',
        component: () =>
          import('@/features/settings/account/AccountPage').then((m) => ({
            default: m.AccountPage,
          })),
      },
      {
        path: 'return-reasons',
        component: () =>
          import('@/features/settings/return-reasons/ReturnReasonsPage').then((m) => ({
            default: m.ReturnReasonsPage,
          })),
      },
      {
        path: 'locations',
        component: () =>
          import('@/features/stock-locations/StockLocationsPage').then((m) => ({
            default: m.StockLocationsPage,
          })),
      },
      {
        path: 'payments',
        component: () =>
          import('@/features/settings/payments/PaymentsPage').then((m) => ({
            default: m.PaymentsPage,
          })),
      },
    ],
  },

  // Content CMS Routes
  {
    path: '/journal',
    component: () =>
      import('@/features/content-cms/JournalListPage').then((m) => ({
        default: m.JournalListPage,
      })),
  },
  {
    path: '/journal/new',
    component: () =>
      import('@/features/content-cms/JournalEditPage').then((m) => ({
        default: m.JournalEditPage,
      })),
  },
  {
    path: '/journal/:id',
    component: () =>
      import('@/features/content-cms/JournalEditPage').then((m) => ({
        default: m.JournalEditPage,
      })),
  },
  {
    path: '/policies',
    component: () =>
      import('@/features/content-cms/PoliciesListPage').then((m) => ({
        default: m.PoliciesListPage,
      })),
  },
  {
    path: '/policies/:slug',
    component: () =>
      import('@/features/content-cms/PolicyEditPage').then((m) => ({
        default: m.PolicyEditPage,
      })),
  },
  {
    path: '/site-config',
    component: () =>
      import('@/features/content-cms/ContentConfigPage').then((m) => ({
        default: m.ContentConfigPage,
      })),
  },
  {
    path: '/faq',
    component: () =>
      import('@/features/content-cms/FAQListPage').then((m) => ({
        default: m.FAQListPage,
      })),
  },
  {
    path: '/testimonials',
    component: () =>
      import('@/features/content-cms/TestimonialsListPage').then((m) => ({
        default: m.TestimonialsListPage,
      })),
  },

  // System & Infrastructure
  {
    path: '/system/dlq',
    component: () => import('@/features/system/DlqPage').then((m) => ({ default: m.DlqPage })),
  },

  // 404 fallback
  {
    path: '*',
    component: () =>
      import('@/features/system/NotFoundPage').then((m) => ({ default: m.NotFoundPage })),
  },
];
